/**
 * componentStyles.js
 * Shared component styles for consistent UI across the application
 */

import { colors, spacing, borderRadius, shadows } from './styleConstants';

// Card styles for boxes and items
export const cardStyles = {
  root: {
    borderRadius: borderRadius.lg,
    boxShadow: shadows.md,
    transition: 'all 0.2s ease-in-out',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: shadows.lg,
    },
  },
  media: {
    height: 0,
    paddingTop: '75%', // 4:3 aspect ratio
    backgroundColor: 'rgba(0,0,0,0.04)',
    position: 'relative',
  },
  content: {
    padding: spacing.md,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: spacing.sm,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
  },
  description: {
    color: colors.mediumSlateBlue,
    fontSize: '0.875rem',
    marginBottom: spacing.sm,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: spacing.sm,
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(61, 72, 90, 0.05)',
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  placeholderIcon: {
    fontSize: '3rem',
    color: colors.mediumSlateBlue,
    opacity: 0.5,
  },
};

// Grid layout styles
export const gridStyles = {
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: spacing.xl,
    padding: spacing.md,
  },
  containerCompact: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: spacing.lg,
    padding: spacing.md,
  },
};

// Form styles
export const formStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
    width: '100%',
  },
  field: {
    marginBottom: spacing.md,
  },
  fieldGroup: {
    display: 'flex',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: spacing.lg,
    gap: spacing.md,
  },
  submitButton: {
    marginTop: spacing.md,
  },
};

// Button styles
export const buttonStyles = {
  primary: {
    backgroundColor: colors.actionBlue,
    color: colors.softWhite,
    '&:hover': {
      backgroundColor: colors.actionBlue,
      transform: 'translateY(-2px)',
      boxShadow: shadows.md,
    },
  },
  secondary: {
    backgroundColor: colors.mediumSlateBlue,
    color: colors.softWhite,
    '&:hover': {
      backgroundColor: colors.mediumSlateBlue,
      transform: 'translateY(-2px)',
      boxShadow: shadows.md,
    },
  },
  outlined: {
    borderColor: colors.actionBlue,
    color: colors.actionBlue,
    '&:hover': {
      borderColor: colors.actionBlue,
      backgroundColor: 'rgba(59, 130, 246, 0.04)',
    },
  },
  text: {
    color: colors.actionBlue,
    '&:hover': {
      backgroundColor: 'rgba(59, 130, 246, 0.04)',
    },
  },
  icon: {
    minWidth: '40px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

// Modal styles
export const modalStyles = {
  paper: {
    borderRadius: borderRadius.lg,
    boxShadow: shadows.xl,
    padding: spacing.lg,
    maxWidth: '95vw',
    width: '500px',
    maxHeight: '90vh',
    margin: spacing.md,
  },
  title: {
    borderBottom: `1px solid ${colors.divider}`,
    paddingBottom: spacing.md,
    marginBottom: spacing.lg,
    color: colors.darkSlateBlue,
    fontWeight: 600,
  },
  content: {
    padding: 0,
    overflowY: 'auto',
  },
  actions: {
    padding: `${spacing.md} 0 0`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing.md,
  },
};

// Dashboard styles
export const dashboardStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.lg,
    padding: spacing.md,
  },
  section: {
    marginBottom: spacing.xl,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.darkSlateBlue,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: spacing.md,
  },
  statCard: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    backgroundColor: colors.softWhite,
    boxShadow: shadows.sm,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: shadows.md,
    },
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: spacing.xs,
  },
  statLabel: {
    fontSize: '0.875rem',
    color: colors.mediumSlateBlue,
  },
};

// Tag styles
export const tagStyles = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  tag: {
    backgroundColor: 'rgba(94, 108, 132, 0.1)',
    color: colors.mediumSlateBlue,
    borderRadius: borderRadius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: '0.75rem',
    display: 'inline-flex',
    alignItems: 'center',
    margin: `${spacing.xs} ${spacing.xs} 0 0`,
  },
  icon: {
    fontSize: '0.875rem',
    marginLeft: spacing.xs,
    cursor: 'pointer',
  },
};

// Search styles
export const searchStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.softWhite,
    borderRadius: borderRadius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    boxShadow: shadows.sm,
    transition: 'all 0.2s',
    '&:focus-within': {
      boxShadow: shadows.md,
    },
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    padding: `${spacing.xs} ${spacing.sm}`,
    backgroundColor: 'transparent',
    color: colors.charcoal,
    fontSize: '1rem',
    '&::placeholder': {
      color: colors.mediumSlateBlue,
    },
  },
  icon: {
    color: colors.mediumSlateBlue,
  },
};

// Image placeholder styles
export const placeholderStyles = {
  box: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(61, 72, 90, 0.05)',
    height: '100%',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: borderRadius.md,
  },
  boxIcon: {
    fontSize: '3.5rem',
    color: colors.mediumSlateBlue,
    opacity: 0.5,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(61, 72, 90, 0.05)',
    height: '100%',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: borderRadius.md,
  },
  itemIcon: {
    fontSize: '3rem',
    color: colors.actionBlue,
    opacity: 0.5,
  },
};

// Export everything as a single object
export const componentStyles = {
  card: cardStyles,
  grid: gridStyles,
  form: formStyles,
  button: buttonStyles,
  modal: modalStyles,
  dashboard: dashboardStyles,
  tag: tagStyles,
  search: searchStyles,
  placeholder: placeholderStyles,
};

export default componentStyles;
